import type { DocumentVersionApprover } from "@models";
import type { JSONContent } from "@tiptap/core";
import { ROUTES } from "src/config";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { getAxiosClient } from "src/services/axios-client";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { z } from "zod";
import {
  AssistantProcessStatus,
  BillingInterval,
  DocumentVersionApproverStatus,
  PatchAssistantProcessBody,
  PatchDashboardCardPreferencesBody,
  Plan,
  SkillListFilter,
  SkillPatchPayload,
  SkillWritePayload,
  TaskDefinitionType,
  TaskStateScope,
  TraceabilityMatrix,
  User,
  UserSurveyAnswers,
  UserUiState,
} from "../stores/models";
import {
  CreateDemoDeviceRequest,
  CreateDeviceUiState,
  DocStatus,
  Fields,
  IntegrationType,
  PatchDocumentVersionData,
  RegulatoryType,
} from "../types";

export const axiosClient = getAxiosClient(window._env_.BACKEND_BASE_URL);

export const getUser = async () => {
  return axiosClient.get(`user/me`);
};

export type SavedChangeManagementPlan = {
  id: string;
  organizationId: string;
  createdAt: string;
  plan: Record<string, unknown>;
};

export const getChangeManagementPlan = async (
  orgId: string,
  planId: string
) => {
  return axiosClient.get<SavedChangeManagementPlan>(
    `organization/${orgId}/change-management-plan/${planId}`
  );
};

export type ChangeManagementPlanListItem = Omit<
  SavedChangeManagementPlan,
  "organizationId"
>;

export const getChangeManagementPlans = async (orgId: string) => {
  return axiosClient.get<ChangeManagementPlanListItem[]>(
    `organization/${orgId}/change-management-plan`
  );
};

export const getUserById = async (userId: string) => {
  return axiosClient.get(`user/${userId}`);
};

export const getUserTodos = async (orgId: string, deviceId?: string) => {
  const query = deviceId ? `?deviceId=${deviceId}` : "";
  return axiosClient.get(`organization/${orgId}/user/me/todos${query}`);
};

export type UserTodosSummary = {
  totalCount: number;
  approvalsCount: number;
  qualityEventCount: number;
  assignApproversCount: number;
  manualCount: number;
  trainingCount: number;
};

export const getUserTodosSummary = async (
  orgId: string,
  deviceId?: string
): Promise<{ data: UserTodosSummary }> => {
  const query = deviceId ? `?deviceId=${deviceId}` : "";
  return axiosClient.get(`organization/${orgId}/user/tasks/summary${query}`);
};

export const getDashboard = async (orgId: string, deviceId?: string) => {
  return axiosClient.get(`organization/${orgId}/dashboard`, {
    params: {
      ...(deviceId && { deviceId }),
    },
  });
};

export type TiptapCloudDocumentResponse = {
  documentName: string;
  content: JSONContent;
};

export type TiptapCollaborationTokenResponse = {
  appId: string;
  token: string;
  allowedDocumentNames: string[];
  expiresInSeconds: number;
  userId: string;
};

export const getTiptapCloudDocument = async ({
  orgId,
  documentName,
}: {
  orgId: string;
  documentName: string;
}) => {
  return axiosClient.get<TiptapCloudDocumentResponse>(
    `organization/${orgId}/tiptap/document`,
    {
      params: { documentName },
    }
  );
};

export const getTiptapCollaborationToken = async ({
  orgId,
}: {
  orgId: string;
}) => {
  return axiosClient.get<TiptapCollaborationTokenResponse>(
    `organization/${orgId}/tiptap/collaboration-token`
  );
};

export const notifyTiptapCommentMentions = async ({
  orgId,
  documentId,
  threadId,
  commentContent,
  previousCommentContent,
}: {
  orgId: string;
  documentId: string;
  threadId: string;
  commentContent: string;
  previousCommentContent?: string;
}) => {
  return axiosClient.post(`organization/${orgId}/tiptap/comment-mention`, {
    documentId,
    threadId,
    commentContent,
    previousCommentContent,
  });
};

export const exportTiptapPdf = async ({
  document,
  fileName,
  orgId,
}: {
  document: JSONContent;
  fileName: string;
  orgId: string;
}) => {
  return axiosClient.post<Blob>(
    `organization/${orgId}/tiptap/export/pdf`,
    {
      doc: document,
      fileName,
    },
    {
      headers: {
        Accept: "application/pdf",
        "Content-Type": "application/json",
      },
      responseType: "blob",
    }
  );
};

export const exportUploadedDocumentArchive = async ({
  documentId,
  orgId,
  versionId,
}: {
  documentId: string;
  orgId: string;
  versionId: string;
}) => {
  return axiosClient.get<Blob>(
    `organization/${orgId}/document/${documentId}/version/${versionId}/uploaded-document-archive`,
    {
      headers: {
        Accept: "application/zip",
      },
      responseType: "blob",
    }
  );
};

export const patchDashboardCardPreferences = async (
  orgId: string,
  cardKey: string,
  data: PatchDashboardCardPreferencesBody
) => {
  return axiosClient.patch(
    `organization/${orgId}/dashboard/preferences/${cardKey}`,
    data
  );
};

export const getUserSurveyAnswers = async () => {
  return axiosClient.get(`user/me/survey-answers`);
};

export const getUserDataKeys = async () => {
  return axiosClient.get("user/data-keys");
};

export const getDataKeyDefinitions = async () => {
  return axiosClient.get("data-keys");
};

export const getSkills = async ({
  orgId,
  filter = "all",
  search = "",
}: {
  orgId: string;
  filter?: SkillListFilter;
  search?: string;
}) => {
  return axiosClient.get(`organization/${orgId}/skill`, {
    params: {
      filter,
      ...(search.trim() && { search: search.trim() }),
    },
  });
};

export const getSkill = async ({
  orgId,
  skillId,
}: {
  orgId: string;
  skillId: string;
}) => {
  return axiosClient.get(`organization/${orgId}/skill/${skillId}`);
};

export const createSkill = async ({
  orgId,
  data,
}: {
  orgId: string;
  data: SkillWritePayload;
}) => {
  return axiosClient.post(`organization/${orgId}/skill`, data);
};

export const patchSkill = async ({
  orgId,
  skillId,
  data,
}: {
  orgId: string;
  skillId: string;
  data: SkillPatchPayload;
}) => {
  return axiosClient.patch(`organization/${orgId}/skill/${skillId}`, data);
};

export const setSkillEnabled = async ({
  orgId,
  skillId,
  enabled,
}: {
  orgId: string;
  skillId: string;
  enabled: boolean;
}) => {
  return axiosClient.patch(`organization/${orgId}/skill/${skillId}/enabled`, {
    enabled,
  });
};

export const duplicateSkill = async ({
  orgId,
  skillId,
  data,
}: {
  orgId: string;
  skillId: string;
  data?: Partial<SkillWritePayload>;
}) => {
  return axiosClient.post(
    `organization/${orgId}/skill/${skillId}/duplicate`,
    data ?? {}
  );
};

export const deleteSkill = async ({
  orgId,
  skillId,
}: {
  orgId: string;
  skillId: string;
}) => {
  return axiosClient.delete(`organization/${orgId}/skill/${skillId}`);
};

export const patchUserSurveyAnswers = async (
  data: Partial<UserSurveyAnswers>
) => {
  return axiosClient.patch(`user/me/survey-answers`, data);
};

export const getDocumentApprovers = async ({
  documentId,
  versionId,
  orgId,
}: {
  documentId: string;
  versionId: string;
  orgId: string;
}) => {
  return axiosClient.get(
    `organization/${orgId}/document/${documentId}/version/${versionId}/approver`
  );
};

export const getUsersByOrg = async ({
  orgId,
  skip = 0,
}: {
  orgId: string;
  skip?: number;
}) => {
  return axiosClient.get(`organization/${orgId}/user`, {
    params: {
      skip,
    },
  });
};

export const patchUserInOrg = async (
  orgId: string,
  id: string,
  data: Partial<User>
) => {
  return axiosClient.patch(`organization/${orgId}/user/${id}`, data);
};

export const updateOrg = async (
  orgId: string,
  data: { name?: string; companyDescription?: string | null }
) => {
  return axiosClient.patch(`organization/${orgId}`, data);
};

export const updateUserUiState = async (data: Partial<UserUiState>) => {
  return axiosClient.patch("user/me/ui-state", data);
};

export const patchUserMe = async (data: Partial<User>) => {
  return axiosClient.patch("user/me", data);
};

export const createEcfrPin = async (pin: string) => {
  return axiosClient.post("user/me/ecfr-pin", { pin });
};

export const updateEcfrPin = async (pin: string) => {
  return axiosClient.put("user/me/ecfr-pin", { pin });
};

export const validateEcfrPin = async (pin: string) => {
  return axiosClient.post<{ isValid: boolean }>("user/me/ecfr-pin/validate", {
    pin,
  });
};

export const sendPinResetEmail = async () => {
  return axiosClient.post("user/me/ecfr-pin/reset-email");
};

export const getDevices = async (orgId: string) => {
  return axiosClient.get(`organization/${orgId}/device`);
};

export const getDevice = async (orgId: string, id: string) => {
  return axiosClient.get(`organization/${orgId}/device/${id}`);
};

export const getTraceabilityMatrix = async (
  orgId: string,
  deviceId: string
): Promise<{ data: TraceabilityMatrix }> => {
  return axiosClient.get(
    `organization/${orgId}/device/${deviceId}/traceability-matrix`
  );
};

export const getDataKeyValue = async (
  orgId: string,
  deviceId: string,
  dataKey: string
): Promise<{ data: { value: string } }> => {
  return axiosClient.get(
    `organization/${orgId}/device/${deviceId}/data-key/${encodeURIComponent(
      dataKey
    )}`
  );
};

export const postSsrDesignFileLink = async ({
  orgId,
  deviceId,
  ssrId,
  documentId,
}: {
  orgId: string;
  deviceId: string;
  ssrId: string;
  documentId: string;
}) => {
  return axiosClient.post(
    `organization/${orgId}/device/${deviceId}/traceability-matrix/ssr-design-file`,
    { ssrId, documentId }
  );
};

export const deleteSsrDesignFileLink = async ({
  orgId,
  deviceId,
  ssrId,
  documentId,
}: {
  orgId: string;
  deviceId: string;
  ssrId: string;
  documentId: string;
}) => {
  return axiosClient.delete(
    `organization/${orgId}/device/${deviceId}/traceability-matrix/ssr-design-file`,
    { data: { ssrId, documentId } }
  );
};

export const postRiskControlSsrLink = async ({
  orgId,
  deviceId,
  rcId,
  ssrId,
}: {
  orgId: string;
  deviceId: string;
  rcId: string;
  ssrId: string;
}) => {
  return axiosClient.post(
    `organization/${orgId}/device/${deviceId}/traceability-matrix/risk-control-ssr-link`,
    { rcId, ssrId }
  );
};

export const deleteRiskControlSsrLink = async ({
  orgId,
  deviceId,
  rcId,
  ssrId,
}: {
  orgId: string;
  deviceId: string;
  rcId: string;
  ssrId: string;
}) => {
  return axiosClient.delete(
    `organization/${orgId}/device/${deviceId}/traceability-matrix/risk-control-ssr-link`,
    { data: { rcId, ssrId } }
  );
};

export const postTestValidationFileLink = async ({
  orgId,
  deviceId,
  testId,
  documentId,
}: {
  orgId: string;
  deviceId: string;
  testId: string;
  documentId: string;
}) => {
  return axiosClient.post(
    `organization/${orgId}/device/${deviceId}/traceability-matrix/test-validation-file`,
    { testId, documentId }
  );
};

export const deleteTestValidationFileLink = async ({
  orgId,
  deviceId,
  testId,
  documentId,
}: {
  orgId: string;
  deviceId: string;
  testId: string;
  documentId: string;
}) => {
  return axiosClient.delete(
    `organization/${orgId}/device/${deviceId}/traceability-matrix/test-validation-file`,
    { data: { testId, documentId } }
  );
};

export const getAnswerItemsByTypeId = async ({
  orgId,
  type,
}: {
  orgId: string;
  type: string;
}) => {
  return axiosClient.get(`organization/${orgId}/search`, {
    params: { type },
  });
};

export const getDataKeys = async () => {
  return axiosClient.get(`user/data-keys`);
};

export const getAnswerItemsById = async ({
  orgId,
  itemId,
}: {
  orgId: string;
  itemId: string;
}) => {
  return axiosClient.get(`organization/${orgId}/search/${itemId}`);
};

export const postDocumentAnswerItem = async ({
  data,
  orgId,
  docId,
  versionId,
  answerId,
}: {
  data: {
    type: string;
    fields: Fields;
  };
  orgId: string;
  docId: string;
  versionId: string;
  answerId: string;
}) => {
  return axiosClient.post(
    `organization/${orgId}/document/${docId}/version/${versionId}/answer/${answerId}/item`,
    data
  );
};

export const patchDocumentAnswer = async ({
  orgId,
  docId,
  versionId,
  data,
}: {
  orgId: string;
  docId: string;
  versionId: string;
  data: { element: string; answer?: string | null; answerFileId?: string };
}) => {
  return axiosClient.patch(
    `organization/${orgId}/document/${docId}/version/${versionId}/answer`,
    data
  );
};

export const patchDocumentAnswerItem = async ({
  orgId,
  docId,
  versionId,
  answerId,
  archived,
  itemId,
  data,
}: {
  orgId: string;
  docId: string;
  versionId: string;
  answerId: string;
  itemId: string;
  archived: boolean;
  data: { fields: Fields };
}) => {
  return axiosClient.patch(
    `organization/${orgId}/document/${docId}/version/${versionId}/answer/${answerId}/item/${itemId}`,
    { ...data, archived }
  );
};

export const postDevice = async (
  orgId: string,
  data: {
    name: string;
    description: string;
  }
) => {
  return axiosClient.post(`organization/${orgId}/device`, data);
};

export const postDemoDevice = async (
  orgId: string,
  data: CreateDemoDeviceRequest
) => {
  return axiosClient.post(`organization/${orgId}/device/demo`, data);
};

export const patchDocument = async (
  orgId: string,
  docId: string,
  data: {
    deviceId?: string | null;
    regulatoryType?: string;
    functionalGroupId?: string;
    folderId?: string | null;
    title?: string;
  }
) => {
  return axiosClient.patch(`/organization/${orgId}/document/${docId}`, {
    ...(data.deviceId !== undefined && { deviceId: data.deviceId }),
    ...(data.regulatoryType && { regulatoryType: data.regulatoryType }),
    ...(data.functionalGroupId && {
      functionalGroupId: data.functionalGroupId,
    }),
    ...(data.folderId !== undefined && {
      folderId: data.folderId,
    }),
    ...(data.title && { title: data.title }),
  });
};

export const patchDevice = async ({
  orgId,
  data,
  deviceId,
}: {
  orgId: string;
  data: Partial<CreateDeviceUiState>;
  deviceId: string;
}) => {
  return axiosClient.patch(`organization/${orgId}/device/${deviceId}`, data);
};

export const deleteDevice = async (orgId: string, id: string) => {
  return axiosClient.delete(`organization/${orgId}/device/${id}`);
};

export const patchRoadmapTasks = async (
  orgId: string,
  deviceId: string,
  data: Record<string, boolean>
) => {
  return axiosClient.patch(
    `organization/${orgId}/device/${deviceId}/roadmap/tasks`,
    data
  );
};

export const getDocument = async (orgId: string, docId: string) => {
  return axiosClient.get(`organization/${orgId}/document/${docId}`);
};

export const getDynamicContent = async (
  orgId: string,
  docId: string,
  body: { versionId?: string; dataKeys: string[] }
) => {
  return axiosClient.post(
    `organization/${orgId}/document/${docId}/dynamic-content`,
    body
  );
};

export const getDocuments = async (
  orgId: string,
  deviceId?: string,
  options?: {
    documentIds?: string[];
    allVersions?: boolean;
    includeOrgDocs?: boolean;
    view?: "explorer" | "references" | "trash";
  }
) => {
  const includeOrgDocs = options?.includeOrgDocs ?? true;

  return axiosClient.get(`organization/${orgId}/document`, {
    params: {
      ...(options?.documentIds && { documentIds: options.documentIds.join(",") }),
      ...(options?.allVersions && { allVersions: true }),
      ...(deviceId && { deviceId }),
      ...(includeOrgDocs && { includeOrgDocs }),
      ...(options?.view && { view: options.view }),
    },
  });
};

export const getFolders = async (orgId: string) => {
  return axiosClient.get(`organization/${orgId}/folders`);
};

export const createFolder = async ({
  orgId,
  data,
}: {
  orgId: string;
  data: {
    name: string;
    parentFolderId: string | null;
  };
}) => {
  return axiosClient.post(`organization/${orgId}/folders`, data);
};

export const patchFolder = async ({
  orgId,
  folderId,
  data,
}: {
  orgId: string;
  folderId: string;
  data: {
    name?: string;
    parentFolderId?: string | null;
  };
}) => {
  return axiosClient.patch(`organization/${orgId}/folders/${folderId}`, data);
};

export const deleteFolder = async ({
  orgId,
  folderId,
}: {
  orgId: string;
  folderId: string;
}) => {
  return axiosClient.delete(`organization/${orgId}/folders/${folderId}`);
};

export const getDocumentsTemplates = async (
  orgId: string,
  deviceId: string
) => {
  return deviceId
    ? axiosClient.get(
        `/organization/${orgId}/device/${deviceId}/document-template`
      )
    : axiosClient.get(`/organization/${orgId}/document-template`, {
        params: { scope: "org" },
      });
};

export const getDocumentExplorerTemplates = async (orgId: string) => {
  return axiosClient.get(`/organization/${orgId}/document-template`, {
    params: {
      scope: "explorer",
    },
  });
};

export const getFrameTemplateById = async (
  orgId: string,
  templateId: string
) => {
  return axiosClient.get(
    `/organization/${orgId}/document-template/by-id/${templateId}`
  );
};

/** A single hierarchy-resolved template by key; hidden/deprecated still resolve. */
export const getFrameTemplateByKey = async (
  orgId: string,
  templateKey: string,
  deviceId?: string | null
) => {
  return axiosClient.get(
    deviceId
      ? `/organization/${orgId}/device/${deviceId}/document-template/${templateKey}`
      : `/organization/${orgId}/document-template/${templateKey}`
  );
};

export const getTemplateDynamicContent = async (
  orgId: string,
  body: { deviceId?: string; dataKeys: string[] }
) => {
  return axiosClient.post(
    `/organization/${orgId}/document-template/dynamic-content`,
    body
  );
};

export const getRoadmapStages = async (orgId: string, deviceId: string) => {
  return axiosClient.get(
    `/organization/${orgId}/device/${deviceId}/roadmap-stage`
  );
};

export const getRoadmapCards = async (orgId: string, deviceId: string) => {
  return axiosClient.get(
    `/organization/${orgId}/device/${deviceId}/roadmap-card`
  );
};

export const getTaskDefinitions = async (orgId: string, deviceId: string) => {
  return axiosClient.get(
    `/organization/${orgId}/device/${deviceId}/task-definition`
  );
};

// Backward compatibility alias
export const getRoadmapTasks = getTaskDefinitions;

// Admin Task Definition API
export type CreateTaskDefinitionData = {
  title: string;
  taskKey: string;
  taskType: TaskDefinitionType;
  stateScope?: TaskStateScope;
  parentCardKey?: string | null;
  description?: string | null;
  enabled?: boolean;
  order?: number | null;
  visibilityConditions?: Record<string, unknown> | null;
  organizationId?: string | null;
  deviceId?: string | null;
};

export type UpdateTaskDefinitionData = {
  title?: string;
  parentCardKey?: string | null;
  description?: string | null;
  enabled?: boolean;
  order?: number | null;
  visibilityConditions?: Record<string, unknown> | null;
};

export const getAdminTaskDefinitions = async () => {
  return axiosClient.get(`/task-definition`);
};

export const checkTaskKeyExists = async (params: {
  taskKey: string;
  organizationId?: string | null;
  deviceId?: string | null;
}): Promise<boolean> => {
  const { data } = await axiosClient.get<{ exists: boolean }>(
    `/task-definition/check-key-exists`,
    { params }
  );
  return data.exists;
};

export const createTaskDefinition = async (data: CreateTaskDefinitionData) => {
  return axiosClient.post(`/task-definition`, data);
};

export const updateTaskDefinition = async (
  taskId: string,
  data: UpdateTaskDefinitionData
) => {
  return axiosClient.patch(`/task-definition/${taskId}`, data);
};

export const deleteTaskDefinition = async (taskId: string) => {
  return axiosClient.delete(`/task-definition/${taskId}`);
};

export type CreateRoadmapCardData = {
  title: string;
  cardKey: string;
  parentStageKey: string;
  description: string;
  enabled?: boolean;
  order?: number | null;
  helpCenterArticleId?: string | null;
  visibilityConditions?: Record<string, unknown> | null;
  organizationId?: string | null;
  deviceId?: string | null;
};

export type UpdateRoadmapCardData = {
  title?: string;
  parentStageKey?: string;
  description?: string;
  enabled?: boolean;
  order?: number | null;
  helpCenterArticleId?: string | null;
  visibilityConditions?: Record<string, unknown> | null;
};

export const getAdminRoadmapCards = async () => {
  return axiosClient.get(`/roadmap-card`);
};

export const createRoadmapCard = async (data: CreateRoadmapCardData) => {
  return axiosClient.post(`/roadmap-card`, data);
};

export const updateRoadmapCard = async (
  cardId: string,
  data: UpdateRoadmapCardData
) => {
  return axiosClient.patch(`/roadmap-card/${cardId}`, data);
};

export const deleteRoadmapCard = async (cardId: string) => {
  return axiosClient.delete(`/roadmap-card/${cardId}`);
};

export type AdminDocumentTemplateScope = "global" | "organization" | "device";

export const getAdminDocumentTemplates = async ({
  scope,
  orgId,
  deviceId,
}: {
  scope: AdminDocumentTemplateScope;
  orgId?: string;
  deviceId?: string;
}) => {
  return axiosClient.get(`/document-template`, {
    params: {
      scope,
      ...(orgId && { orgId }),
      ...(deviceId && { deviceId }),
    },
  });
};

export type AdminOrganization = {
  id: string;
  name: string;
};

export type AdminDevice = {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string | null;
};

export type AdminRoadmapStageScope = "global" | "organization" | "device";

export const getAdminRoadmapStages = async ({
  scope,
  orgId,
  deviceId,
}: {
  scope: AdminRoadmapStageScope;
  orgId?: string;
  deviceId?: string;
}) => {
  return axiosClient.get(`/roadmap-stage`, {
    params: {
      scope,
      ...(orgId && { orgId }),
      ...(deviceId && { deviceId }),
    },
  });
};

export const getAdminOrganizations = async () => {
  return axiosClient.get(`/admin/organizations`);
};

export const getAdminDevices = async () => {
  return axiosClient.get(`/admin/devices`);
};

export const configureRoadmapCardDisabledTargetsAdmin = async ({
  cardKey,
  scope,
  organizationIds,
  deviceIds,
}: {
  cardKey: string;
  scope: "organization" | "device";
  organizationIds?: string[];
  deviceIds?: string[];
}) => {
  return axiosClient.post(
    `/admin/roadmap-card/${cardKey}/configure-disabled-targets`,
    {
      scope,
      organizationIds,
      deviceIds,
    }
  );
};

export const configureTaskDefinitionDisabledTargetsAdmin = async ({
  taskKey,
  scope,
  organizationIds,
  deviceIds,
}: {
  taskKey: string;
  scope: "organization" | "device";
  organizationIds?: string[];
  deviceIds?: string[];
}) => {
  return axiosClient.post(
    `/admin/task-definition/${taskKey}/configure-disabled-targets`,
    {
      scope,
      organizationIds,
      deviceIds,
    }
  );
};

export type CreateDocumentRequestBody = {
  deviceId?: string;
  templateKey?: string;
  title?: string;
  functionalGroupId?: string;
  regulatoryType?: RegulatoryType;
  userUpload?: boolean;
};

export const createDocument = async (
  orgId: string,
  body?: CreateDocumentRequestBody
) => {
  return axiosClient.post(`organization/${orgId}/document`, body);
};

export const deleteTemplate = async (
  orgId: string,
  deviceId: string,
  templateKey: string
) => {
  return axiosClient.delete(
    `organization/${orgId}/device/${deviceId}/document-template/${templateKey}`
  );
};

export const createDocumentVersion = async (
  orgId: string,
  documentId: string,
  body: {
    file?: File;
    contentTemplate?: string;
    readyForApproval?: boolean;
  }
) => {
  return axiosClient.post(
    `organization/${orgId}/document/${documentId}/version`,
    body,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

export const uploadFile = async ({
  orgId,
  file,
  deviceId,
}: {
  orgId: string;
  file: File;
  deviceId?: string;
}) => {
  return axiosClient.post(
    `organization/${orgId}/file`,
    { file, ...(deviceId && { deviceId }) },
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

export const uploadChatAttachment = async ({
  orgId,
  file,
  deviceId,
}: {
  orgId: string;
  file: File;
  deviceId?: string;
}) => {
  const response = await axiosClient.post(
    `organization/${orgId}/file/attachment`,
    { file, ...(deviceId && { deviceId }) },
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  if (response.status !== 201)
    throw new Error("Attachment upload failed. Retry this file.");
  const data = z
    .object({
      id: z.string().uuid(),
      filename: z.string(),
      mimeType: z.string(),
    })
    .parse(response.data);
  return { ...response, data };
};
export const saveChatAttachmentToEqms = async ({
  orgId,
  deviceId,
  fileId,
}: {
  orgId: string;
  deviceId?: string;
  fileId: string;
}) => {
  const response = await axiosClient.post(
    `organization/${orgId}/document/from-file`,
    { fileId, ...(deviceId && { deviceId }) }
  );
  if (response.status !== 200)
    throw new Error("Could not save the attachment to eQMS.");
  return z
    .object({
      documentId: z.string().uuid(),
      documentVersionId: z.string().uuid(),
    })
    .parse(response.data);
};

export const retryAttachmentParsing = async ({
  orgId,
  fileId,
}: {
  orgId: string;
  fileId: string;
}) => axiosClient.post(`organization/${orgId}/file/${fileId}/retry-parsing`);

export const getFile = async ({
  orgId,
  fileId,
  headers,
}: {
  orgId: string;
  fileId: string;
  headers?: Record<string, string>;
}) => {
  return axiosClient.get(`organization/${orgId}/file/${fileId}`, {
    headers,
    responseType: "blob",
  });
};

export const getAllFilesInOrg = async ({
  orgId,
  deviceId,
}: {
  orgId: string;
  deviceId?: string;
}) => {
  return axiosClient.get(`organization/${orgId}/file`, {
    params: {
      ...(deviceId && { deviceId }),
    },
  });
};

export const deleteFile = async ({
  orgId,
  fileId,
}: {
  orgId: string;
  fileId: string;
}) => {
  return axiosClient.delete(`organization/${orgId}/file/${fileId}`);
};

export const getFileParseStatus = async ({
  orgId,
  fileId,
}: {
  orgId: string;
  fileId: string;
}) => {
  return axiosClient.get(`organization/${orgId}/file/${fileId}/parse-status`);
};

export const getFileShareLink = async ({
  orgId,
  fileId,
}: {
  orgId: string;
  fileId: string;
}) => {
  return axiosClient.get(`organization/${orgId}/file/${fileId}/share-link`);
};

export const postAssistantProcess = async ({
  data,
  orgId,
  deviceId,
}: {
  data: PatchAssistantProcessBody;
  orgId: string;
  deviceId: string;
}) => {
  return axiosClient.post(
    `/organization/${orgId}/device/${deviceId}/assistant-process`,
    data
  );
};

export const getAssistantProcesses = async (
  orgId: string,
  deviceId: string,
  status?: AssistantProcessStatus
) => {
  return axiosClient.get(
    `organization/${orgId}/device/${deviceId}/assistant-process`,
    { params: { ...(status && { status }) } }
  );
};

export const patchAssistantProcess = async (
  orgId: string,
  deviceId: string,
  processId: string,
  data: PatchAssistantProcessBody
) => {
  return axiosClient.patch(
    `organization/${orgId}/device/${deviceId}/assistant-process/${processId}`,
    data
  );
};

export const deleteDocumentVersion = async (
  orgId: string,
  docId: string,
  versionId: string
) => {
  return axiosClient.delete(
    `organization/${orgId}/document/${docId}/version/${versionId}`
  );
};

export const deleteDocumentDrafts = async (orgId: string, docId: string) => {
  return axiosClient.delete(`organization/${orgId}/document/${docId}/drafts`);
};

export const deleteDocument = async (orgId: string, docId: string) => {
  return axiosClient.delete(`organization/${orgId}/document/${docId}`);
};

export const restoreDocument = (orgId: string, docId: string) =>
  axiosClient.post(`organization/${orgId}/document/${docId}/restore`);

export const permanentlyDeleteDocument = (orgId: string, docId: string) =>
  axiosClient.delete(`organization/${orgId}/document/${docId}/permanent`);

export const getTrashedDocument = (orgId: string, docId: string) =>
  axiosClient.get(`organization/${orgId}/document/trash/${docId}`);

export type EmptyDocumentTrashSummary = {
  matched: number;
  deleted: number;
  failed: number;
};

export const emptyDocumentTrash = (orgId: string) =>
  axiosClient.delete<EmptyDocumentTrashSummary>(
    `organization/${orgId}/document/trash`
  );

export const patchDocumentVersion = async (
  orgId: string,
  documentId: string,
  versionId: string,
  data: PatchDocumentVersionData
) => {
  return axiosClient.patch(
    `organization/${orgId}/document/${documentId}/version/${versionId}`,
    data
  );
};

export const patchDocumentVersionDataKeys = async (
  orgId: string,
  documentId: string,
  versionId: string,
  data: { dataKeyKeys: string[] }
) => {
  return axiosClient.patch(
    `organization/${orgId}/document/${documentId}/version/${versionId}/data-keys`,
    data
  );
};

export const postDocumentAnswer = async (
  orgId: string,
  documentId: string,
  versionId: string,
  data: {
    answer?: string | null;
    element: string;
    answerFileId?: string;
  }
) => {
  return axiosClient.patch(
    `organization/${orgId}/document/${documentId}/version/${versionId}/answer`,
    data
  );
};

export const postDocumentAnswerSuggestion = async (
  orgId: string,
  documentId: string,
  versionId: string,
  data: { suggestion: string; element: string; threadId: string }
) => {
  return axiosClient.post(
    `organization/${orgId}/document/${documentId}/version/${versionId}/suggestion`,
    data
  );
};

export const checkout = async (
  orgId: string,
  plan: Plan.PREMIUM | Plan.PREMIUM_PLUS,
  amount: number,
  cycle: BillingInterval
) => {
  return axiosClient.post(`organization/${orgId}/subscription/checkout`, {
    plan,
    amount,
    cycle,
    cancelUrl: window.location.href,
    successUrl: `${window.location.origin}${fillRouteWithSlugs(
      ROUTES.CHECKOUT_SUCCESS,
      {
        [ROUTE_SLUGS.ORG_ID]: orgId,
      }
    )}`,
  });
};

export const postBillingSession = async (orgId: string) => {
  return axiosClient.post(`organization/${orgId}/subscription/billing`, {
    returnUrl: window.location.href,
  });
};

export const postDocumentApprover = async ({
  orgId,
  documentId,
  versionId,
  data,
}: {
  orgId: string;
  documentId: string;
  versionId: string;
  data: { userId: string };
}) => {
  return axiosClient.post(
    `organization/${orgId}/document/${documentId}/version/${versionId}/approver`,
    data
  );
};

export const replaceDocumentApprovers = async ({
  orgId,
  documentId,
  versionId,
  userIds,
}: {
  orgId: string;
  documentId: string;
  versionId: string;
  userIds: string[];
}) =>
  axiosClient.put<DocumentVersionApprover[]>(
    `organization/${orgId}/document/${documentId}/version/${versionId}/approver`,
    { userIds }
  );

export const patchApproverStatus = async ({
  orgId,
  documentId,
  versionId,
  status,
}: {
  orgId: string;
  documentId: string;
  versionId: string;
  status: keyof typeof DocumentVersionApproverStatus;
}) => {
  return axiosClient.patch(
    `organization/${orgId}/document/${documentId}/version/${versionId}/approver/me`,
    {
      status,
    }
  );
};

export const deleteDocumentApprover = async (
  orgId: string,
  documentId: string,
  versionId: string,
  approverId: string
) => {
  return axiosClient.delete(
    `organization/${orgId}/document/${documentId}/version/${versionId}/approver/${approverId}`
  );
};

export const postUserInvite = async (
  orgId: string,
  data: {
    inviteeEmail: string;
    inviteeFirstName: string;
    inviteeLastName: string;
  }
) => {
  return axiosClient.post(`organization/${orgId}/user/invite`, data);
};

export const deleteUserFromOrg = async (orgId: string, userId: string) => {
  return axiosClient.delete(`organization/${orgId}/user/${userId}`);
};

export const reactivateUserInOrg = async (orgId: string, userId: string) => {
  return axiosClient.patch(`organization/${orgId}/user/${userId}/activate`);
};

export const postCustomTemplate = async (
  orgId: string,
  deviceId: string,
  data: {
    templateKey: string;
    defaultTitle: string;
    // functionalGroupId: string;
  }
) => {
  return axiosClient.post(
    `/organization/${orgId}/device/${deviceId}/document-template`,
    data
  );
};

export const patchCustomTemplate = async (
  orgId: string,
  deviceId: string,
  templateKey: string,
  data: {
    defaultTitle?: string;
    functionalGroupAcronym?: string | null;
    regulatoryType?: RegulatoryType;
    contentTemplate?: string;
  }
) => {
  return axiosClient.patch(
    `/organization/${orgId}/device/${deviceId}/document-template/${templateKey}`,
    data
  );
};

export const getIntegration = async (orgId: string, type: IntegrationType) => {
  return axiosClient.get(`organization/${orgId}/integration/${type}`);
};

export const disconnectIntegration = async (
  orgId: string,
  type: IntegrationType
) => {
  return axiosClient.delete(`organization/${orgId}/integration/${type}`);
};

export const setIntegrationEnabled = async (
  orgId: string,
  type: IntegrationType,
  enabled: boolean
) => {
  return axiosClient.patch(
    `organization/${orgId}/integration/${type}/enabled`,
    {
      enabled,
    }
  );
};

export const getSlackAuthUrl = async (orgId: string) => {
  return axiosClient.get(`organization/${orgId}/integration/slack/auth/url`);
};

export const getLinearAuthUrl = async (orgId: string) => {
  return axiosClient.get(`organization/${orgId}/integration/linear/auth/url`);
};

export const getGithubAuthUrl = async (orgId: string) => {
  return axiosClient.get(`organization/${orgId}/integration/github/auth/url`);
};

export const getJiraAuthUrl = async (orgId: string) => {
  return axiosClient.get(`organization/${orgId}/integration/jira/auth/url`);
};

import type { McpConnectionPresetKey } from "src/config/mcp-presets";

export const getMcpConnections = async (orgId: string) => {
  return axiosClient.get(`organization/${orgId}/mcp-connection`);
};

export const createManualMcpConnection = async (
  orgId: string,
  data: { name: string; serverUrl: string; accessToken: string }
) => {
  return axiosClient.post(`organization/${orgId}/mcp-connection/manual`, data);
};

export const createAuthlessMcpConnection = async (
  orgId: string,
  data: { name: string; serverUrl: string; presetKey?: McpConnectionPresetKey }
) => {
  return axiosClient.post(
    `organization/${orgId}/mcp-connection/authless`,
    data
  );
};

export const startMcpOAuthConnection = async (
  orgId: string,
  data: { name: string; serverUrl: string; presetKey?: McpConnectionPresetKey }
) => {
  return axiosClient.post(
    `organization/${orgId}/mcp-connection/oauth/start`,
    data
  );
};

export const disconnectMcpConnection = async (
  orgId: string,
  connectionId: string
) => {
  return axiosClient.delete(
    `organization/${orgId}/mcp-connection/${connectionId}`
  );
};

export const setMcpConnectionEnabled = async (
  orgId: string,
  connectionId: string,
  enabled: boolean
) => {
  return axiosClient.patch(
    `organization/${orgId}/mcp-connection/${connectionId}/enabled`,
    {
      enabled,
    }
  );
};

export const testMcpConnection = async (
  orgId: string,
  connectionId: string
) => {
  return axiosClient.post(
    `organization/${orgId}/mcp-connection/${connectionId}/test`
  );
};

export const sendSlackTest = async (orgId: string, message?: string) => {
  return axiosClient.post(`organization/${orgId}/integration/slack/test`, {
    message,
  });
};

// Task State API
export const getTaskStates = async (orgId: string, deviceId: string) => {
  return axiosClient.get(`organization/${orgId}/device/${deviceId}/task-state`);
};

export const getTaskState = async (
  orgId: string,
  deviceId: string,
  stateId: string
) => {
  return axiosClient.get(
    `organization/${orgId}/device/${deviceId}/task-state/${stateId}`
  );
};

export const getTaskStateActivity = async (
  orgId: string,
  deviceId: string,
  stateId: string
) => {
  return axiosClient.get(
    `organization/${orgId}/device/${deviceId}/task-state/${stateId}/activity`
  );
};

export const createTaskState = async (
  orgId: string,
  deviceId: string,
  data: {
    taskKey: string;
    assigneeId?: string | null;
    dueDate?: string | null;
    manualStatus?: "TODO" | "IN_PROGRESS" | "DONE";
  }
) => {
  return axiosClient.post(
    `organization/${orgId}/device/${deviceId}/task-state`,
    data
  );
};

export const attachTaskStateDocument = async (
  orgId: string,
  deviceId: string,
  stateId: string,
  data: {
    documentId: string;
  }
) => {
  return axiosClient.post(
    `organization/${orgId}/device/${deviceId}/task-state/${stateId}/document`,
    data
  );
};

export const updateTaskState = async (
  orgId: string,
  deviceId: string,
  stateId: string,
  data: {
    assigneeId?: string | null;
    dueDate?: string | null;
    manualStatus?: "TODO" | "IN_PROGRESS" | "DONE";
  }
) => {
  return axiosClient.patch(
    `organization/${orgId}/device/${deviceId}/task-state/${stateId}`,
    data
  );
};

export const attachFileDocumentToTaskState = async (
  orgId: string,
  deviceId: string,
  stateId: string,
  fileDocumentId: string
) => {
  return axiosClient.post(
    `organization/${orgId}/device/${deviceId}/task-state/${stateId}/file-document/${fileDocumentId}`
  );
};

export const removeFileFromTaskState = async (
  orgId: string,
  deviceId: string,
  stateId: string,
  fileDocumentId: string
) => {
  return axiosClient.delete(
    `organization/${orgId}/device/${deviceId}/task-state/${stateId}/file-document/${fileDocumentId}`
  );
};

// Organization Tasks API
export type TaskScope = "mine" | "all";

export type TaskFileDocument = {
  id: string;
  title: string | null;
  filename: string | null;
  shareToken: string | null;
};

export type UnifiedTask = {
  id: string;
  taskType: "MANUAL" | "DOCUMENT" | "APPROVAL" | "QUALITY_EVENT" | "TRAINING";
  title: string;
  description: string | null;
  status: string;
  docStatus?: DocStatus | null;
  assignee: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  dueDate: string | null;
  device: {
    id: string;
    name: string;
  } | null;
  taskKey: string | null;
  parentCardKey?: string | null;
  documentId: string | null;
  templateKey: string | null;
  createdBy: string | null;
  fileDocuments: TaskFileDocument[];
  actionPath?: string | null;
  qualityEventRecordId?: string | null;
  qualityEventKey?: string | null;
  qualityEventStepKey?: string | null;
  // TRAINING-only fields (null for other task types)
  sourceOccurrenceId?: string | null;
  sourceProgramId?: string | null;
  sourceProgramName?: string | null;
  documentVersionIdToTrainOn?: string | null;
  trainingQuizId?: string | null;
  trainingReadAt?: string | null;
  trainingQuizPassedAt?: string | null;
  trainingSignedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export const getOrgTasks = async (
  orgId: string,
  scope: TaskScope = "mine"
): Promise<{ data: UnifiedTask[] }> => {
  return axiosClient.get(`organization/${orgId}/user/tasks?scope=${scope}`);
};

// Ad-hoc task creation
// If deviceId is provided, task has DEVICE scope
// If deviceId is not provided, task has ORGANIZATION scope (shows on card for all devices)
export type CreateAdHocTaskData = {
  title: string;
  description?: string | null;
  parentCardKey?: string | null;
  deviceId?: string | null;
  assigneeId?: string | null;
};

export type UpdateAdHocTaskData = {
  title?: string;
  description?: string | null;
  parentCardKey?: string | null;
  deviceId?: string | null;
};

export const createAdHocTask = async (
  orgId: string,
  data: CreateAdHocTaskData
) => {
  return axiosClient.post(`organization/${orgId}/adhoc-task`, data);
};

export const deleteAdHocTask = async (orgId: string, taskKey: string) => {
  return axiosClient.delete(`organization/${orgId}/adhoc-task/${taskKey}`);
};

export const updateAdHocTask = async (
  orgId: string,
  taskKey: string,
  data: UpdateAdHocTaskData
) => {
  return axiosClient.patch(`organization/${orgId}/adhoc-task/${taskKey}`, data);
};

export const disableRoadmapCard = async ({
  orgId,
  deviceId,
  cardKey,
  scope,
}: {
  orgId: string;
  deviceId: string;
  cardKey: string;
  scope: "organization" | "device";
}) => {
  return axiosClient.post(
    `organization/${orgId}/device/${deviceId}/roadmap-card/${cardKey}/disable`,
    { scope }
  );
};

export type CreateFunctionalGroupData = {
  name: string;
  acronym: string;
  administerAllTrainings?: boolean;
};

export type PatchFunctionalGroupData = {
  name?: string;
  acronym?: string;
  administerAllTrainings?: boolean;
};

export const getFunctionalGroups = async (orgId: string) => {
  return axiosClient.get(`organization/${orgId}/functional-group`);
};

export const createFunctionalGroup = async (
  orgId: string,
  data: CreateFunctionalGroupData
) => {
  return axiosClient.post(`organization/${orgId}/functional-group`, data);
};

export const patchFunctionalGroup = async (
  orgId: string,
  groupId: string,
  data: PatchFunctionalGroupData
) => {
  return axiosClient.patch(
    `organization/${orgId}/functional-group/${groupId}`,
    data
  );
};

export const deleteFunctionalGroup = async (orgId: string, groupId: string) => {
  return axiosClient.delete(
    `organization/${orgId}/functional-group/${groupId}`
  );
};

export const addFunctionalGroupMember = async (
  orgId: string,
  groupId: string,
  data: { organizationMembershipId: string }
) => {
  return axiosClient.post(
    `organization/${orgId}/functional-group/${groupId}/member`,
    data
  );
};

export const removeFunctionalGroupMember = async (
  orgId: string,
  groupId: string,
  membershipId: string
) => {
  return axiosClient.delete(
    `organization/${orgId}/functional-group/${groupId}/member/${membershipId}`
  );
};

// ---------------------------------------------------------------------------
// Task Programs (TRAINING)
// ---------------------------------------------------------------------------

export type TrainingScope = "mine" | "all";
export type TrainingCompletionMethod = "ACKNOWLEDGEMENT" | "QUIZ";
export type TrainingQuizState = "DRAFT" | "PUBLISHED";

export type TrainingQuizQuestion = {
  id: string;
  text: string;
  options: Array<{ id: string; text: string }>;
  correctOptionId: string;
};

export type TrainingQuizSummary = {
  documentVersionId: string;
  publishedAt: string | null;
};

export type TrainingQuiz = TrainingQuizSummary & {
  id: string;
  programId: string;
  questions: TrainingQuizQuestion[];
  publishedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PublishTrainingQuizResult = {
  quiz: TrainingQuiz;
  occurrenceId: string | null;
  createdTaskCount: number;
  alreadyPublished: boolean;
};

export type DeleteTrainingQuizResult = {
  deletedQuiz: boolean;
  deletedTaskCount: number;
};

export type TraineeTrainingQuiz = {
  id: string;
  documentVersionId: string;
  questions: Array<{
    id: string;
    text: string;
    options: Array<{ id: string; text: string }>;
  }>;
  passed: boolean;
};

export type TrainingQuizSubmissionResult = { passed: boolean };

export type TaskProgramAssignmentInput = {
  functionalGroupId?: string | null;
  userId?: string | null;
};

export type TaskProgramAssignment = {
  id: string;
  programId: string;
  functionalGroupId: string | null;
  userId: string | null;
  functionalGroup: {
    id: string;
    name: string;
    acronym: string;
  } | null;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  createdAt: string;
};

export const TASK_PROGRAM_DUE_AFTER_DAYS_OPTIONS = [
  7, 14, 30, 180, 365,
] as const;
export type TaskProgramDueAfterDays =
  (typeof TASK_PROGRAM_DUE_AFTER_DAYS_OPTIONS)[number];

export type TaskProgram = {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  linkedDocumentId: string;
  enabled: boolean;
  completionMethod: TrainingCompletionMethod;
  dueAfterDays: TaskProgramDueAfterDays | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  assignments: TaskProgramAssignment[];
  linkedDocument: {
    id: string;
    title: string;
    deviceId: string | null;
    versions?: { id: string; revision: number | null }[];
  };
  creator: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  quizzes: TrainingQuizSummary[];
  latestOccurrence?: TaskProgramOccurrenceSummary | null;
};

export type TaskProgramOccurrenceSummary = {
  id: string;
  triggeredAt: string;
  quizId: string | null;
  documentVersion: { id: string; revision: number | null } | null;
  totalTasks: number;
  completedTasks: number;
};

export type TaskProgramOccurrenceDetail = {
  id: string;
  triggeredAt: string;
  triggeredBy: string | null;
  triggeredByUser: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  documentVersion: { id: string; revision: number | null } | null;
  quizId: string | null;
  tasks: {
    id: string;
    assigneeId: string | null;
    assignee: {
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
      functionalGroups: { id: string; name: string; acronym: string }[];
    } | null;
    manualStatus: "TODO" | "IN_PROGRESS" | "DONE";
    trainingReadAt: string | null;
    trainingQuizPassedAt: string | null;
    trainingSignedAt: string | null;
    dueDate: string | null;
  }[];
};

export type TaskProgramExpectedUser = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  functionalGroups: { id: string; name: string; acronym: string }[];
  directlyAssigned: boolean;
};

export type TaskProgramFireableRevision = {
  documentVersionId: string;
  revision: number;
  hasOccurrence: boolean;
};

export type TaskProgramDraftRevision = {
  documentVersionId: string;
  revision: number;
};

export type TaskProgramDetail = TaskProgram & {
  occurrences: TaskProgramOccurrenceDetail[];
  expectedUserCount: number;
  expectedUsers: TaskProgramExpectedUser[];
  /**
   * True when an occurrence already exists for the linked document's current
   * LIVE revision. Null when no live revision exists yet.
   */
  alreadyFiredForCurrentRevision: boolean | null;
  /**
   * Every LIVE revision of the linked document, ordered newest first, with
   * `hasOccurrence` flagging which revisions have already been fired.
   */
  fireableRevisions: TaskProgramFireableRevision[];
  /**
   * The current draft and the revision number it will receive when approved.
   */
  draftRevision: TaskProgramDraftRevision | null;
};

export type CreateTaskProgramData = {
  name: string;
  description?: string | null;
  linkedDocumentId: string;
  enabled?: boolean;
  completionMethod?: TrainingCompletionMethod;
  dueAfterDays?: TaskProgramDueAfterDays | null;
  assignments: TaskProgramAssignmentInput[];
};

export type UpdateTaskProgramData = {
  name?: string;
  description?: string | null;
  enabled?: boolean;
  completionMethod?: TrainingCompletionMethod;
  dueAfterDays?: TaskProgramDueAfterDays | null;
  assignments?: TaskProgramAssignmentInput[];
};

export type BulkAssignTaskProgramsData = {
  documentIds: string[];
  functionalGroupIds: string[];
  userIds: string[];
  completionMethod?: TrainingCompletionMethod;
  dueAfterDays?: TaskProgramDueAfterDays;
};

export type BulkAssignTaskProgramsResult = {
  createdProgramCount: number;
  updatedProgramCount: number;
  addedAssignmentCount: number;
  createdOccurrenceCount: number;
  attachedOccurrenceCount: number;
  createdTaskCount: number;
  skippedOccurrenceCount: number;
  pendingQuizCount: number;
  programs: TaskProgram[];
};

export const getTaskPrograms = async (
  orgId: string,
  scope: TrainingScope = "mine"
): Promise<{ data: TaskProgram[] }> => {
  return axiosClient.get(`organization/${orgId}/task-programs?scope=${scope}`);
};

export const getTaskProgramDetail = async (
  orgId: string,
  programId: string
): Promise<{ data: TaskProgramDetail }> => {
  return axiosClient.get(
    `organization/${orgId}/task-programs/${programId}/detail`
  );
};

export const createTaskProgram = async (
  orgId: string,
  data: CreateTaskProgramData
): Promise<{ data: TaskProgram }> => {
  return axiosClient.post(`organization/${orgId}/task-programs`, data);
};

export const bulkAssignTaskPrograms = async (
  orgId: string,
  data: BulkAssignTaskProgramsData
): Promise<{ data: BulkAssignTaskProgramsResult }> => {
  return axiosClient.post(
    `organization/${orgId}/task-programs/bulk-assign-documents`,
    data
  );
};

export const updateTaskProgram = async (
  orgId: string,
  programId: string,
  data: UpdateTaskProgramData
): Promise<{ data: TaskProgram }> => {
  return axiosClient.patch(
    `organization/${orgId}/task-programs/${programId}`,
    data
  );
};

export const getTrainingQuiz = async (
  orgId: string,
  programId: string,
  documentVersionId: string
): Promise<{ data: TrainingQuiz | null }> => {
  return axiosClient.get(
    `organization/${orgId}/task-programs/${programId}/quiz?documentVersionId=${documentVersionId}`
  );
};

export const deleteTrainingQuiz = async (
  orgId: string,
  programId: string,
  documentVersionId: string,
  expectedUpdatedAt: string,
  expectedState: TrainingQuizState,
  confirmedCompletedTaskIds: string[]
): Promise<{ data: DeleteTrainingQuizResult }> => {
  return axiosClient.delete(
    `organization/${orgId}/task-programs/${programId}/quiz`,
    {
      data: {
        documentVersionId,
        expectedUpdatedAt,
        expectedState,
        confirmedCompletedTaskIds,
      },
    }
  );
};

export const publishTrainingQuiz = async (
  orgId: string,
  programId: string,
  documentVersionId: string
): Promise<{ data: PublishTrainingQuizResult }> => {
  return axiosClient.post(
    `organization/${orgId}/task-programs/${programId}/quiz/publish`,
    { documentVersionId }
  );
};

export const deleteTaskProgram = async (orgId: string, programId: string) => {
  return axiosClient.delete(`organization/${orgId}/task-programs/${programId}`);
};

export const triggerTaskProgramForRevision = async (
  orgId: string,
  programId: string,
  documentVersionId: string
): Promise<{ data: { occurrenceId: string; createdTaskCount: number } }> => {
  return axiosClient.post(
    `organization/${orgId}/task-programs/${programId}/trigger/revisions/${documentVersionId}`
  );
};

export const deleteTaskProgramOccurrence = async (
  orgId: string,
  programId: string,
  occurrenceId: string
): Promise<{ data: { deletedTaskCount: number } }> => {
  return axiosClient.delete(
    `organization/${orgId}/task-programs/${programId}/occurrences/${occurrenceId}`
  );
};

export type AddOccurrenceTraineeData = {
  functionalGroupId?: string | null;
  userId?: string | null;
  alsoAddToProgram?: boolean;
};

export const addTaskProgramOccurrenceTrainee = async (
  orgId: string,
  programId: string,
  occurrenceId: string,
  data: AddOccurrenceTraineeData
): Promise<{
  data: { createdTaskCount: number; addedToProgram: boolean };
}> => {
  return axiosClient.post(
    `organization/${orgId}/task-programs/${programId}/occurrences/${occurrenceId}/trainees`,
    data
  );
};

export const deleteTaskProgramOccurrenceTask = async (
  orgId: string,
  programId: string,
  occurrenceId: string,
  taskStateId: string
) => {
  return axiosClient.delete(
    `organization/${orgId}/task-programs/${programId}/occurrences/${occurrenceId}/tasks/${taskStateId}`
  );
};

// ---------------------------------------------------------------------------
// Training Task actions (per-assignee)
// ---------------------------------------------------------------------------

export const markTrainingTaskRead = async (
  orgId: string,
  stateId: string
): Promise<{ data: UnifiedTask }> => {
  return axiosClient.post(
    `organization/${orgId}/training-task/${stateId}/mark-read`
  );
};

export const signTrainingTask = async (
  orgId: string,
  stateId: string,
  pin: string
): Promise<{ data: UnifiedTask }> => {
  return axiosClient.post(
    `organization/${orgId}/training-task/${stateId}/sign`,
    { pin }
  );
};

export const getTraineeTrainingQuiz = async (
  orgId: string,
  stateId: string
): Promise<{ data: TraineeTrainingQuiz }> => {
  return axiosClient.get(`organization/${orgId}/training-task/${stateId}/quiz`);
};

export const submitTrainingQuiz = async (
  orgId: string,
  stateId: string,
  answers: Array<{ questionId: string; optionId: string }>
): Promise<{ data: TrainingQuizSubmissionResult }> => {
  return axiosClient.post(
    `organization/${orgId}/training-task/${stateId}/quiz/submit`,
    { answers }
  );
};
